import { useLocation, useNavigate } from 'react-router';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import useShallowEqualSelector from '../../core/useShallowEqualSelector';
import { useIsActive } from './hooks';
import { layerHandleContent, setLayer } from '../../../global-state/redux/actions';
import {
  addLayerPrefix,
  adminRoles,
  buttonsArr,
  classValueDesktop,
  container,
  layers,
  layerStorageKey,
  storageValueDesktop
} from './utils';
import './layerControl.css';
import { useSettingsWithManage } from '../../../modules/manage-config';
import { useBookedTickets } from '../../../shared/m-anage';
import { emptyArray } from '../../../shared/misc';

const LayerControl = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const localStorageValue = localStorage.getItem(layerStorageKey);
  const { superUserTicketName } = useSettingsWithManage('manageConfig') ?? {};
  const isActive = useIsActive();
  const unlockContentRedux = useShallowEqualSelector((state) => state?.appState?.unlockContent);
  const manageAdminUserRoles = useShallowEqualSelector(
    (state) => state?.user?.login_info?.manage_admin_user_roles
  );
  const { data: tickets = emptyArray } = useBookedTickets();
  const haveAdminManageRole = manageAdminUserRoles?.some((el) => adminRoles.includes(el.title));
  const haveSuperUserTicket = tickets?.some((el) => el.TicketTypeName === superUserTicketName);
  const isAdmin = !!(isActive && (haveAdminManageRole || haveSuperUserTicket));
  const [shouldShow, setShouldShow] = useState(false);
  const [showButtons, setShowButtons] = useState(false);
  const searchParams = new URLSearchParams(location.search);
  const containerParams = searchParams.get(container);

  const layerFromParams = buttonsArr.find(
    (el) => el?.storage && el?.storage?.toLocaleLowerCase() === containerParams?.toLocaleLowerCase()
  );
  const layerFromStorage = buttonsArr.find(
    (el) => el?.storage && el?.storage?.toLocaleLowerCase() === localStorageValue?.toLocaleLowerCase()
  );

  // priority: 1. params 2. storage 3. default
  const label = layerFromParams?.label || layerFromStorage?.label || classValueDesktop; // default is desktop
  const storage = layerFromParams?.storage || layerFromStorage?.storage || storageValueDesktop; // default is desktop
  const [selectedLayer, setSelectedLayer] = useState({ label, storage });
  const [showHover, setShowHover] = useState(null);

  const handleClick = (el) => {
    if (el?.storage) {
      setSelectedLayer({ label: el.label, storage: el.storage });
      dispatch(setLayer(el.storage));
    }
    if (el?.redux) {
      dispatch(layerHandleContent(el.redux)); // Lock/Unlock content
    }
  };

  const shouldShowHover = (val) => (showHover === val ? <h6 className="layer-popup">{val}</h6> : null);

  useEffect(() => {
    setShouldShow(isAdmin);
  }, [isAdmin]);

  useEffect(() => {
    // Add class to the root element (layerApp/layerDekstop/layerTerminal) + set localStorage
    const root = document.getElementById('vmRevolutionRoot');
    if (root && selectedLayer) {
      root.classList.remove(...buttonsArr.map((el) => addLayerPrefix(el?.label)));
      root.classList.add(addLayerPrefix(selectedLayer?.label));
      // Set localStorage to { layer: App/VMX/Terminal } | VMX = Desktop in PAG
      if (selectedLayer?.storage) {
        localStorage.setItem(layerStorageKey, selectedLayer?.storage);
        dispatch(setLayer(selectedLayer?.storage));
        searchParams.delete(container);
        navigate(`?${searchParams.toString()}`);
      }
    }
  }, [selectedLayer, shouldShow]);

  if (!shouldShow) return null;

  const mobileNavbar = document.getElementsByClassName('mobile-navigation-root');
  let mobileNavbarHeight;
  if (mobileNavbar?.length > 0) {
    mobileNavbarHeight = mobileNavbar[0]?.offsetHeight;
  }

  return (
    <div className="layer-wrapper" style={{ bottom: `calc(${mobileNavbarHeight ?? 5}px + 5px)` }}>
      {/* buttonsArr contain un.lock content + layers, un.lock content not set the storage, just dispatch action, layers set storage dispatch action and set params */}
      {buttonsArr.map((el) => (
        <>
          {el.redux ? (
            // lock & unlock content
            ((el.showWhenContentUnlocked && unlockContentRedux)
              || (!el.showWhenContentUnlocked && !unlockContentRedux)) && (
              <button
                key={el.label}
                type="button"
                className={`layer-button ${!showButtons ? 'layer-hidden' : ''} selected-layer`}
                onClick={() => handleClick(el)}
                onMouseEnter={() => setShowHover(el.label)}
                onMouseLeave={() => setShowHover(null)}
              >
                {shouldShowHover(el.label)}
                {el.icon}
              </button>
            ) // layers
          ) : (
            <button
              aria-label={el.label}
              key={el.label}
              type="button"
              className={`layer-button ${!showButtons ? 'layer-hidden' : ''} ${
                selectedLayer?.label === el?.label ? 'selected-layer' : ''
              }`}
              onClick={() => handleClick(el)}
              onMouseEnter={() => setShowHover(el.label)}
              onMouseLeave={() => setShowHover(null)}
            >
              {shouldShowHover(el.label)}
              {el.icon}
            </button>
          )}
        </>
      ))}
      <button
        type="button"
        aria-label="Show/Hide layers"
        onClick={() => setShowButtons((prevStats) => !prevStats)}
        className="layer-button show-layers"
        onMouseEnter={() => setShowHover(layers)}
        onMouseLeave={() => setShowHover(null)}
      >
        {shouldShowHover(layers)}
        {showButtons ? (
          <i className="fa-solid fa-xmark" />
        ) : (
          <i className="fa-solid fa-layer-group" />
        )}
      </button>
    </div>
  );
};

export default LayerControl;
